import { displayValue, calculateDiscount } from './priceUtils';
import {
  getTagValueFromTagEngine,
  getPriceLableFromTagEngine,
  getSellingFastTagFromTagEngine,
  getPLPTagV2FromPosition
} from '../../utils/product';
import { gtmEventPush, segmentEventPush } from '../../modules/ga/GTMdatalayer';
import { formatDate } from './dateUtils';

const validFormats = ['productGrid3ListingImage', 'mobileProductListingImage', 'product'];

interface ProductFlags {
  isUrgencyDriverV2PLPEnabled?: boolean;
}

const getProductProps = (
  products: import('../types/product-page-types').ProductDetails[] = [],
  { isUrgencyDriverV2PLPEnabled = false }: ProductFlags = {}
): import('../types/product-page-types').SnRProductProps[] =>
  products.map((prdt, index) => {
    const {
      images,
      name,
      fnlColorVariantData,
      catalog,
      price: { value, segmentId = '', experimentId = '' } = {},
      wasPriceData: { value: totalValue } = {},
      url,
      code,
      variantOptions,
      offerPrice,
      isGiftWithPurchase = false,
      brickName,
      brickCategory,
      sourceStoreId,
      tags: { categoryTags = [] } = {},
      averageRating,
      numUserRatings,
      ratingCount,
      catalogName,
      segmentName,
      verticalName,
      urgencyDriverTagResponse: { positions = [] } = {},
      uclid = '',
      exclusiveTill = ''
    } = prdt;

    let mainImage = '';
    images?.forEach(element => {
      if (validFormats.includes(element.format)) {
        mainImage = element.url;
      }
    });
    const firstSizeVariantCode = variantOptions?.[0].code;
    const disPrice = displayValue(value);
    const outOfStockProduct = variantOptions?.some(product => product.stock.stockLevel <= 0);
    const origPrice = displayValue(totalValue);
    const disPercentage = calculateDiscount(totalValue, value);
    const PLPTagV2TopLeft = isUrgencyDriverV2PLPEnabled
      ? getPLPTagV2FromPosition(positions, 'PRODUCT_TILE_IMAGE_TOP_LEFT')?.shortText
      : '';
    const productTagEngineValue = PLPTagV2TopLeft || getTagValueFromTagEngine(categoryTags);
    const productPriceLabel = getPriceLableFromTagEngine(categoryTags);
    const productSellingFastTag = getSellingFastTagFromTagEngine(categoryTags);
    //  console.log("prdt",prdt)
    return {
      id: index,
      mainImage,
      description: name,
      brand: fnlColorVariantData?.brandName,
      model: name,
      currentPrice: disPrice,
      actualPrice: disPrice !== origPrice ? origPrice : '',
      discount: disPrice !== origPrice ? disPercentage : '',
      descriptionPageURL: url,
      code,
      catalogName: catalog || catalogName,
      firstSizeVariantCode,
      offerPrice,
      isGiftWithPurchase,
      priceValue: value,
      brickName,
      productTag: fnlColorVariantData?.productTag || '',
      productTagEngineValue,
      deliverySlaTag: fnlColorVariantData?.deliverySlaTag || '',
      productSellingFastTag,
      productPriceLabel,
      isExclusive: fnlColorVariantData?.exclusiveTill || exclusiveTill,
      productColor: fnlColorVariantData?.colorGroup || '',
      productCategory: brickCategory,
      productSize: '',
      sourceStoreId,
      averageRating,
      numUserRatings: numUserRatings || ratingCount,
      segmentId,
      experimentId,
      outOfStockProduct: outOfStockProduct,
      slaDeliveryTag:fnlColorVariantData?.slaDeliveryTag || '',                 //returning new PD/RUSH details in crossSell redux
      deliveryMethod:fnlColorVariantData?.deliveryMathod || fnlColorVariantData?.deliveryMethod || '',
      segmentName,
      verticalName,
      uclid: uclid // only add if its an osmos call
    };
  });

interface Variants {
  sizeVariant: import('../types/product-page-types').VariantOption[];
  colorVariant: import('../types/product-page-types').BaseOption | undefined;
}

const extractVariantsFromProduct = (
  productDetails: import('../types/product-page-types').ProductDetails
): Variants => {
  const { baseOptions = [], variantOptions } = productDetails || {};
  let sizeVariant;
  let colorVariant;
  for (let i = 0; i < baseOptions.length; i += 1) {
    switch (baseOptions[i].variantType) {
      case 'FnlColorVariant':
        colorVariant = baseOptions[i];
        break;
      case 'FnlSizeVariant':
        sizeVariant = baseOptions[i].options;
        break;
      default:
    }
  }
  if (!sizeVariant) {
    sizeVariant = variantOptions;
  }
  return { sizeVariant, colorVariant };
};

interface ExtractStandardSize {
  displaySizeFormate: string | undefined;
}

// Return product standard
const getStandardSize = (
  productDetails: import('../types/product-page-types').ProductDetails
): ExtractStandardSize => {
  const { baseOptions = [], variantOptions } = productDetails;
  let displaySizeFormate;
  displaySizeFormate =
    baseOptions[0] && baseOptions[0].options[0] && baseOptions[0].options[0].displaySizeFormat;
  if (displaySizeFormate) {
    displaySizeFormate = variantOptions[0].displaySizeFormat;
  }
  return { displaySizeFormate };
};

interface ExtractFeaturedBrandSize {
  featureStandardFormate: string | undefined;
}
// Return product brand standard formate
const getBrandDetails = (
  productDetails: import('../types/product-page-types').ProductDetails
): ExtractFeaturedBrandSize => {
  const { featureData = [] } = productDetails;
  let featureStandardFormate;
  featureStandardFormate = featureData.reduce(
    (res, data) => (
      data.name === 'Size Format' ||
        (data.name === 'sizeFormat' && res.push(data.featureValues[0])),
      res
    ),
    []
  )[0];
  return { featureStandardFormate };
};

interface ExtractInfoFromSelection {
  appendDesc: boolean;
  hideSizes: boolean;
  metalPurity: boolean;
  qualifierKey: string;
  qualifierKeysForDesc: string[];
  jewelleryProduct: boolean;
}

const extractSelectionValues = (
  colorVariant: import('../types/product-page-types').BaseOption | undefined
): ExtractInfoFromSelection => {
  const { selection1, selection2, skuVisibleAttributes = '', classDesc } =
    colorVariant?.selected?.attributes?.reduce<Record<string, string>>(
      (acc, item) => ({ ...acc, [item.name]: item.value }),
      {}
    ) || {};
  let appendDesc = false;
  let hideSizes = false;
  let metalPurity = false;
  let qualifierKey = 'size';
  let qualifierKeysForDesc: string[] = [];
  const jewelleryProduct = classDesc === 'Fine Jewellery';

  if (selection1 || selection2) {
    // case when volume is there then volume is shown in place of sizes and add volume in description too
    appendDesc =
      // (selection1 === 'NULL' || !selection1) &&
      !!selection2 && selection2.toLowerCase() !== 'size';
    if (selection2 && selection2.toLowerCase() === 'null') appendDesc = false;
    if (appendDesc) qualifierKey = selection2.toLowerCase();
    // case when only color is there then no sizes need to display so hide size select section
    hideSizes =
      (selection2 === 'NULL' || !selection2) &&
      !!selection1 &&
      selection1.toLowerCase() === 'color';

    // selection 1  = ?  metalPurity     // metalpurity behave like color
    metalPurity = !!selection1 && selection1.toLowerCase() === 'metalpurity';
    // if selection1 is metalpurity(not color) then add in description
    // if selection2 is except size then add in producti description

    // TODO: product details me sku vissible
    qualifierKeysForDesc = skuVisibleAttributes?.split(',');
  }
  return {
    hideSizes,
    appendDesc,
    // variantKey,
    metalPurity,
    qualifierKey,
    qualifierKeysForDesc,
    jewelleryProduct
  };
};

interface Item {
  qualifier: string;
  value: string;
  name: string;
}

const getQualifierToValueMap = (acc: Record<string, string>, item: Item): Record<string, string> =>
  item.qualifier ? { ...acc, [item.qualifier.toLowerCase()]: item.value } : { ...acc };

interface SizeNameArgument {
  variantOptionQualifiers: import('../types/product-page-types').VariantOptionQualifier[];
  scDisplaySize: string;
  appendDesc: boolean;
  newSizeRevampFlag: boolean;
  qualifierKey: string;
}

const getSizeName = ({
  variantOptionQualifiers,
  newSizeRevampFlag,
  scDisplaySize,
  appendDesc,
  qualifierKey
}: SizeNameArgument): string => {
  let sizeName = '';
  const qualifierObj: Record<string, string> = variantOptionQualifiers.reduce(
    getQualifierToValueMap,
    {}
  );
  const { standardSize, size, uom = '' } = qualifierObj;
  const value = qualifierObj[qualifierKey];
  if (appendDesc && value) {
    sizeName = `${value} ${uom}`;
    return sizeName;
  }
  if (newSizeRevampFlag && scDisplaySize) {
    return scDisplaySize;
  }
  sizeName = size || standardSize;
  return sizeName;
};

const getSizeFormat = (
  variantOptionQualifiers: import('../types/product-page-types').VariantOptionQualifier[]
): string => {
  let sizeFormat = '';
  variantOptionQualifiers.forEach(({ qualifier, value: qualifierValue }) => {
    if (['sizeFormat', 'standardSizeFormat'].includes(qualifier) && qualifierValue) {
      sizeFormat = qualifierValue;
    }
  });
  return sizeFormat;
};

const checkDevice = device => {
  return navigator.userAgent.toUpperCase().indexOf(device);
};

const getDeepLinkUrl = () => {
  let deepLinkUrl;
  if (checkDevice('IPAD') > -1 || checkDevice('IPHONE') > -1) {
    deepLinkUrl =
      'https://itunes.apple.com/in/app/ajio-online-shopping-app/id1113425372?rutm_campaign=Organic_App_Install&utm_source=App_Microsite_Mobile&utm_medium=App_Store_Badge';
  } else if (checkDevice('ANDROID') > -1) {
    deepLinkUrl =
      'https://play.google.com/store/apps/details?id=com.ril.ajio&referrer=utm_source%3DApp_Microsite_Mobile%26utm_medium%3DPlay_Store_Badge%26utm_campaign%3DOrganic_App_Install';
  } else {
    deepLinkUrl = '#';
  }
  return deepLinkUrl;
};

const openSubscriptionDialog = (membershipData: {}, setSubscriptionModal) => {
    let isSubscriptionModalImpressionFired = sessionStorage.getItem('isSubscriptionModalImpressionFired');

    if (isSubscriptionModalImpressionFired !== "true") {
      sessionStorage.setItem('isSubscriptionModalImpressionFired', "true");
      gtmEventPush({
        event: 'pageload',
        page_name: 'subscription benifits halfcard screen',
        subscription_details: `${membershipData?.status || 'NA'} | ${membershipData?.detail?.productId || 'NA'} | ${membershipData?.detail?.identifier || 'NA'} | NA`, // TODO: please add the value of subscription_version and sub_no;
      });
    }

    setSubscriptionModal(true);
  };

const hideSubscriptionDialog = (membershipData: {}, setSubscriptionModal) => {
  const { detail, status, userDetail } = membershipData || {};

  gtmEventPush({
    event: 'subscription_benifits_interactions',
    event_category: 'subscription benifits screen interaction',
    event_action: 'close',
    page_name: 'subscription benifits halfcard screen',
    subscription_details: `${status || 'NA'} | ${detail?.productId ||
      'NA'} | ${detail?.identifier || 'NA'} | NA` // TODO: please add the value of subscription_version and sub_no;
  });

  segmentEventPush({
    event: 'Subscription Benefits Halfcard Clicked',
    page_name: 'pdp screen',
    subscription_mrp: detail?.price?.mrp,
    subscription_list_price: detail?.price?.listPrice,
    click_cta_name: 'close',
    subscription_status: status,
    subscription_version: detail?.identifier,
    subscription_id: detail?.productId,
    subscription_start_date: userDetail?.boughtOn ? formatDate(userDetail?.boughtOn) : 'NA',
    subscription_end_date: userDetail?.validTill ? formatDate(userDetail?.validTill) : 'NA',
    subscribed_orders_left: userDetail?.orderLeft || 'NA',
    total_account_savings: userDetail?.totalSavings || 'NA',
    platform_account_savings: userDetail?.savingBreakup?.platformFeeSaving || 'NA',
    delivery_account_savings: userDetail?.savingBreakup?.deliveryFeeSaving || 'NA',
    membership_discount_account_savings: userDetail?.savingBreakup?.membershipSaving || 'NA',
    subscription_first_purcase_date: 'NA',
    subscripiton_number: 'NA'
  });

  setSubscriptionModal(false);
};

const filterOutDuplicateProduct = (fixedArray = [] , variableArray = [], key = 'code') => {
  if (!fixedArray.length || !fixedArray[0]?.[key]) return variableArray;

  const fixedProduct = fixedArray[0];

  return variableArray.filter(item => item[key] !== fixedProduct[key]);
}

export {
  getProductProps,
  displayValue,
  calculateDiscount,
  extractVariantsFromProduct,
  getStandardSize,
  getBrandDetails,
  extractSelectionValues,
  getSizeName,
  getSizeFormat,
  getDeepLinkUrl,
  openSubscriptionDialog,
  hideSubscriptionDialog,
  filterOutDuplicateProduct
};
